// ======================================================================
// FILE: src/modules/wms/calendar/components/CalendarHolidaysTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Calendar Management
// COMPONENT: Holidays Tab
//
// PURPOSE:
//   Configure Holiday Calendar
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsCalendarService
from "../../services/wmsCalendarService";

const DEFAULT_FORM = {

  holiday_code: "",
  holiday_name: "",

  holiday_date: "",

  holiday_type: "PUBLIC",

  is_recurring: false,

  is_half_day: false,

  half_day_type: "",

  affects_sla: true,

  affects_recurring_jobs: true,

  description: "",
  remarks: "",
};

export default function CalendarHolidaysTab({
  calendarId,
}) {

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showForm, setShowForm] =
    useState(false);

  const [editingId, setEditingId] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  async function loadData() {

    if (!calendarId) {
      return;
    }

    try {

      setLoading(true);

      const data =
        await wmsCalendarService
          .getHolidays(calendarId);

      setRows(data || []);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load holidays"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {

    loadData();

  }, [calendarId]);

  // ==========================================================
  // ADD
  // ==========================================================

  function handleAdd() {

    setEditingId(null);

    setForm(DEFAULT_FORM);

    setShowForm(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingId(
      row.id
    );

    setForm({

      holiday_code:
        row.holiday_code || "",

      holiday_name:
        row.holiday_name || "",

      holiday_date:
        row.holiday_date || "",

      holiday_type:
        row.holiday_type || "PUBLIC",

      is_recurring:
        row.is_recurring || false,

      is_half_day:
        row.is_half_day || false,

      half_day_type:
        row.half_day_type || "",

      affects_sla:
        row.affects_sla ?? true,

      affects_recurring_jobs:
        row.affects_recurring_jobs ?? true,

      description:
        row.description || "",

      remarks:
        row.remarks || "",
    });

    setShowForm(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.holiday_code) {

      alert(
        "Holiday Code is required"
      );

      return;
    }

    if (!form.holiday_name) {

      alert(
        "Holiday Name is required"
      );

      return;
    }

    if (!form.holiday_date) {

      alert(
        "Holiday Date is required"
      );

      return;
    }

    try {

      setSaving(true);

      if (editingId) {

        await wmsCalendarService
          .updateHoliday(
            calendarId,
            editingId,
            form
          );

      } else {

        await wmsCalendarService
          .createHoliday(
            calendarId,
            form
          );
      }

      setShowForm(false);

      await loadData();

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to save holiday"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Holiday ${row.holiday_name}?`
    );

    if (!ok) return;

    try {

      await wmsCalendarService
        .deleteHoliday(
          calendarId,
          row.id
        );

      await loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  if (!calendarId) {

    return (

      <div className="alert alert-info">

        Save calendar first
        before configuring holidays.

      </div>
    );
  }

  return (

    <div>

      {/* HEADER */}

      <div
        className="d-flex justify-content-between align-items-center mb-3"
      >

        <h5>
          Holiday Configuration
        </h5>

        <button
          className="btn btn-primary"
          onClick={handleAdd}
        >
          Add Holiday
        </button>

      </div>

      {/* FORM */}

      {showForm && (

        <div className="card mb-3">

          <div className="card-header">

            {
              editingId
                ? "Edit Holiday"
                : "Add Holiday"
            }

          </div>

          <div className="card-body">

            <div className="row">

              <div className="col-md-3">

                <label>
                  Holiday Code
                </label>

                <input
                  className="form-control"
                  value={form.holiday_code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      holiday_code:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-4">

                <label>
                  Holiday Name
                </label>

                <input
                  className="form-control"
                  value={form.holiday_name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      holiday_name:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-3">

                <label>
                  Holiday Date
                </label>

                <input
                  type="date"
                  className="form-control"
                  value={form.holiday_date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      holiday_date:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-2">

                <label>
                  Type
                </label>

                <select
                  className="form-control"
                  value={form.holiday_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      holiday_type:
                        e.target.value,
                    })
                  }
                >

                  <option value="PUBLIC">
                    Public
                  </option>

                  <option value="COMPANY">
                    Company
                  </option>

                  <option value="REGIONAL">
                    Regional
                  </option>

                </select>

              </div>

            </div>

            <div className="row mt-3">

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_recurring
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_recurring:
                          e.target.checked,
                      })
                    }
                  />

                  <label className="form-check-label">
                    Recurring
                  </label>

                </div>

              </div>

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_half_day
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_half_day:
                          e.target.checked,
                      })
                    }
                  />

                  <label className="form-check-label">
                    Half Day
                  </label>

                </div>

              </div>

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.affects_sla
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        affects_sla:
                          e.target.checked,
                      })
                    }
                  />

                  <label className="form-check-label">
                    Affects SLA
                  </label>

                </div>

              </div>

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.affects_recurring_jobs
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        affects_recurring_jobs:
                          e.target.checked,
                      })
                    }
                  />

                  <label className="form-check-label">
                    Affects Jobs
                  </label>

                </div>

              </div>

            </div>

            <div className="mt-3">

              <button
                className="btn btn-primary me-2"
                onClick={handleSave}
                disabled={saving}
              >
                {
                  saving
                    ? "Saving..."
                    : "Save"
                }
              </button>

              <button
                className="btn btn-secondary"
                onClick={() =>
                  setShowForm(false)
                }
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

      {/* GRID */}

      <div className="card">

        <div className="card-body p-0">

          <table
            className="table table-bordered table-hover mb-0"
          >

            <thead>

              <tr>

                <th>Code</th>
                <th>Name</th>
                <th>Date</th>
                <th>Type</th>
                <th>Recurring</th>
                <th width="140">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>
                  <td
                    colSpan="6"
                    className="text-center"
                  >
                    Loading...
                  </td>
                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>
                  <td
                    colSpan="6"
                    className="text-center"
                  >
                    No Holidays Found
                  </td>
                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.holiday_code}
                  </td>

                  <td>
                    {row.holiday_name}
                  </td>

                  <td>
                    {row.holiday_date}
                  </td>

                  <td>
                    {row.holiday_type}
                  </td>

                  <td>
                    {
                      row.is_recurring
                        ? "Yes"
                        : "No"
                    }
                  </td>

                  <td>

                    <button
                      className="btn btn-sm btn-warning me-1"
                      onClick={() =>
                        handleEdit(row)
                      }
                    >
                      Edit
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        handleDelete(row)
                      }
                    >
                      Delete
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}